"use client";

import { useActionState } from "react";

import { cn } from "@workspace/ui/lib/utils";
import { Button } from "@workspace/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@workspace/ui/components/card";
import { Field, FieldError, FieldGroup, FieldLabel } from "@workspace/ui/components/field";
import { Input } from "@workspace/ui/components/input";
import { Spinner } from "@workspace/ui/components/spinner";

export interface LoginFormState {
  error: string | null;
}

interface LoginFormProps extends React.ComponentProps<"div"> {
  action: (state: LoginFormState, formData: FormData) => LoginFormState | Promise<LoginFormState>;
  callbackUrl?: string;
}

const initialState: LoginFormState = {
  error: null,
};

export function LoginForm({
  action,
  callbackUrl = "/dashboard",
  className,
  ...props
}: LoginFormProps) {
  const [state, formAction, isPending] = useActionState(action, initialState);
  const hasError = state.error !== null;

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>Use your Inventory Nexus operator account.</CardDescription>
        </CardHeader>
        <CardContent>
          <form action={formAction}>
            <input name="callbackUrl" type="hidden" value={callbackUrl} />
            <FieldGroup>
              <Field data-invalid={hasError}>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  aria-invalid={hasError}
                  autoComplete="email"
                  disabled={isPending}
                  id="email"
                  name="email"
                  placeholder="operator@example.com"
                  required
                  type="email"
                />
              </Field>
              <Field data-invalid={hasError}>
                <FieldLabel htmlFor="password">Password</FieldLabel>
                <Input
                  aria-invalid={hasError}
                  autoComplete="current-password"
                  disabled={isPending}
                  id="password"
                  name="password"
                  required
                  type="password"
                />
                <FieldError>{state.error}</FieldError>
              </Field>
              <Field>
                <Button disabled={isPending} type="submit">
                  {isPending ? <Spinner data-icon="inline-start" /> : null}
                  Sign in
                </Button>
              </Field>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
